import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import LookupsApi from "../../API/lookups.api";
import scanFiltersApi from "../../API/scanFilters.api";
import { getObject, setObject } from "../../utils/StorageHelpers";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { ErrorAlert } from "../../utils/Alerts";
import { isEmpty } from "lodash";
import ScanFilterApplied from "../../components/Scans/ScanFilterApplied";
import ScanFiltersForm from "../../components/Scans/ScanFiltersForm";
import { mapLookupToSelect } from "../../utils/Helpers";
import { shortDateYMD } from "../../utils/DateHelper";
import RecentFiltersForm from "../../components/Scans/RecentFiltersForm";
import ManageScansData from "../../components/Scans/ManageScansData";
import { useHistory } from "react-router-dom";
import { LOGIC_TYPE } from "../../utils/constants";
import LoadingOverlay from "../../components/Loaders/LoadingOverlay";

const Scans = () => {
    // Hooks
    const { t } = useTranslation();
    const [materials, setMaterials] = useState([]);
    const [appliedFilter, setAppliedFilter] = useState(null);
    const [editFilter, setEditFilter] = useState(false);
    const [scanFilter, setScanFilter] = useState({});
    const [recentFilters, setRecentFilters] = useState([]);
    const [selectedRecentFilter, setSelectedRecentFilter] = useState(null);
    const [mappingToForm, setMappingToForm] = useState(false);
    const [scans, setScans] = useState({});
    const [materialsLoading, setMaterialLoading] = useState(false);

    const [suppliersLookup, setSuppliersLookup] = useState([]);
    const [lotsLookup, setLotsLookup] = useState([]);

    const history = useHistory();

    const query = new URLSearchParams(location.search);
    const queryMaterialId = query.get("materialId");
    const querySampleName = query.get("sampleName");

    useEffect(() => {
        getMaterialsLookup();
        if (!queryMaterialId && !querySampleName) {
            const LocalScanFilter = getObject("scanFilter");
            if (!isEmpty(LocalScanFilter)) applyFilter(LocalScanFilter);
            else history.push("/scans");
        }
    }, []);

    useEffect(() => {
        if (materials?.length > 0) applySearchParametersFilter(materials);
    }, [queryMaterialId, querySampleName]);

    useEffect(() => {
        const LocalScanFilter = getObject("scanFilter");
        let recentFilterExists;
        if (LocalScanFilter && LocalScanFilter.id)
            recentFilterExists = recentFilters.find(
                (item) => item.id === LocalScanFilter.id
            );
        if (recentFilterExists) setSelectedRecentFilter(recentFilterExists);
    }, [recentFilters]);

    useEffect(() => {
        if (!mappingToForm) {
            setSelectedRecentFilter(null);
        }
    }, [scanFilter]);

    useEffect(() => {
        if (appliedFilter?.material?.value) {
            getLotsLookup();
            getSuppliersLookup();
        }
    }, [appliedFilter?.material?.value]);

    const getLotsLookup = async () => {
        const result = await LookupsApi.getLots(appliedFilter?.material?.value);
        setLotsLookup(result);
    };

    const getSuppliersLookup = async () => {
        const result = await LookupsApi.getSuppliers(
            appliedFilter?.material?.value
        );
        setSuppliersLookup(result);
    };

    const applySearchParametersFilter = (materialsLookup) => {
        const selectedMaterial = materialsLookup.find(
            (material) =>
                material.id === queryMaterialId ||
                material.value === queryMaterialId
        );
        if (!selectedMaterial) return history.push("/scans");
        applyFilter({
            material: {
                label: selectedMaterial?.name ?? selectedMaterial?.label,
                value: queryMaterialId,
            },
            sampleName: querySampleName ?? "",
        });
    };

    const getMaterialsLookup = async () => {
        setMaterialLoading(true);
        const result = await LookupsApi.getUsedMaterialsInScans();
        if (queryMaterialId) {
            applySearchParametersFilter(result);
        }
        setMaterials(
            mapLookupToSelect(
                result.map((material) => ({
                    ...material,
                    materialName: material.name,
                }))
            )
        );
        setMaterialLoading(false);
    };

    const applyFilter = (filter) => {
        setAppliedFilter(filter);
        setScanFilter((prevInput) => ({
            ...prevInput,
            material: filter?.material,
        }));
        disableEditFilter();
        scanFiltersApi
            .createScanFilter({
                ...filter,
                materialId: filter?.material?.value,
            })
            .then((res) => {
                if (res?.inserted) {
                    LogCapture.info(
                        `Scan filter created with material id: ${filter.material?.value}`
                    );
                }
                if (res?.modified) {
                    LogCapture.info(
                        `Scan filter updated with material id: ${filter.material?.value}`
                    );
                }
                setObject("scanFilter", { ...filter, id: res.id });
            })
            .catch((retrievedError) => {
                LogCapture.error(
                    `Couldn't create scan filter with material id: ${filter.material?.value}, error: ${retrievedError}`
                );
                ErrorAlert(t("addScanFilterFailMessage"));
            });
    };

    const enableEditFilter = () => {
        setMappingToForm(true);
        setEditFilter(true);
        mapAppliedFilterToInput().then(() => setMappingToForm(false));
    };

    const thresholdEmptyState = {
        target: "",
        compare: "",
        value: "",
        unit: "",
    };

    const thresholdStartState = {
        type: LOGIC_TYPE.and,
        filters: [thresholdEmptyState, thresholdEmptyState],
    };

    const getIsOutlierValue = (isOutlier) => {
        const value = isOutlier?.toString();

        switch (value) {
            case "true":
                return true;
            case "false":
                return false;
            default:
                return null;
        }
    };

    const applyRecentFilter = (recentFilter) => {
        setMappingToForm(true);
        if (recentFilter?.threshold?.filters?.length === 1)
            recentFilter.threshold.filters.push(thresholdEmptyState);
        setScanFilter({
            material: {
                value: recentFilter.materialId,
                label: recentFilter.material.name,
            },
            models: recentFilter.models,
            devices: recentFilter.devices,
            suppliers: recentFilter.suppliers,
            lots: recentFilter.lots,
            createdByUsers: recentFilter.createdByUsers,
            onBehalfOf: recentFilter.onBehalfOf,
            isOutlier: getIsOutlierValue(recentFilter.isOutlier),
            ...(recentFilter.capturedStartDate && {
                capturedStartDate: shortDateYMD(recentFilter.capturedStartDate),
            }),
            ...(recentFilter.capturedEndDate && {
                capturedEndDate: shortDateYMD(recentFilter.capturedEndDate),
            }),
            ...{ threshold: recentFilter.threshold || thresholdStartState },
            ...(recentFilter.sampleName && {
                sampleName: recentFilter.sampleName,
            }),
            ...(recentFilter.note && {
                note: recentFilter.note,
            }),
        });
        setMappingToForm(false);
    };

    const disableEditFilter = async () => {
        await setMappingToForm(true);
        await setScanFilter({});
        setEditFilter(false);
        setMappingToForm(false);
    };

    const mapAppliedFilterToInput = async () => {
        let threshold = appliedFilter.threshold
            ? {
                  type: appliedFilter.threshold?.type,
                  filters: [...appliedFilter.threshold?.filters],
              }
            : thresholdStartState;
        if (threshold.filters?.length === 1)
            threshold.filters.push(thresholdEmptyState);
        setScanFilter({
            material: appliedFilter.material,
            models: appliedFilter.models,
            devices: appliedFilter.devices,
            suppliers: appliedFilter.suppliers,
            lots: appliedFilter.lots,
            createdByUsers: appliedFilter.createdByUsers,
            onBehalfOf: appliedFilter.onBehalfOf,
            isOutlier: getIsOutlierValue(appliedFilter.isOutlier),
            ...(appliedFilter.capturedStartDate && {
                capturedStartDate: shortDateYMD(
                    appliedFilter.capturedStartDate
                ),
            }),
            ...(appliedFilter.capturedEndDate && {
                capturedEndDate: shortDateYMD(appliedFilter.capturedEndDate),
            }),
            ...{ threshold },
            ...(appliedFilter.sampleName && {
                sampleName: appliedFilter.sampleName,
            }),
            ...(appliedFilter.note && {
                note: appliedFilter.note,
            }),
        });
    };

    const getEditScanForm = () => {
        return (
            <>
                <span className={`${editFilter ? "" : "hide"}`}>
                    <h4 className="text-primary-blue ">
                        {t("selectFromRecent")}
                    </h4>
                    <hr className="mt-1 mb-3" />
                    <RecentFiltersForm
                        recentFilters={recentFilters}
                        setRecentFilters={setRecentFilters}
                        selectedRecentFilter={selectedRecentFilter}
                        setSelectedRecentFilter={setSelectedRecentFilter}
                        applyRecentFilter={applyRecentFilter}
                        materialsLookup={materials}
                    />
                    <h4 className="text-primary-blue ">
                        {t("orApplyCustomFilter")}
                    </h4>
                    <hr className="mt-1 mb-3" />
                    <ScanFiltersForm
                        input={scanFilter}
                        setInput={setScanFilter}
                        materialsLookup={materials}
                        disableEditFilter={disableEditFilter}
                        applyFilter={applyFilter}
                        mappingToForm={mappingToForm}
                        materialsLoading={materialsLoading}
                        thresholdStartState={thresholdStartState}
                        getIsOutlierValue={getIsOutlierValue}
                    />
                </span>
                {!isEmpty(appliedFilter) && (
                    <span className={`${editFilter ? "hide" : ""}`}>
                        <ScanFilterApplied
                            appliedFilter={appliedFilter}
                            enableEditFilter={enableEditFilter}
                            getIsOutlierValue={getIsOutlierValue}
                        />
                    </span>
                )}
            </>
        );
    };

    if (queryMaterialId && !materials.length) return null;

    return (
        <>
            <header className="main-customer-form-header main-content-header flex items-center">
                <h2 className="h2">{t("scans")}</h2>
            </header>
            <div className="card-table shadow mb-5">
                <LoadingOverlay
                    active={queryMaterialId && materialsLoading}
                    text="Loading..."
                >
                    <button
                        className="default-transparent-btn mb-5"
                        onClick={() => history.push("/scans")}
                    >
                        <i className="fas fa-caret-left"></i>
                        <span className="ml-2">{t("goBack")}</span>
                    </button>
                    {getEditScanForm()}
                </LoadingOverlay>
                <hr className="my-4" />
                <ManageScansData
                    appliedFilter={appliedFilter}
                    scans={scans}
                    setScans={setScans}
                    getIsOutlierValue={getIsOutlierValue}
                    suppliersLookup={suppliersLookup}
                    lotsLookup={lotsLookup}
                />
            </div>
        </>
    );
};

export default Scans;
